/**
 * Slot calculation utilities for interview scheduling
 */

/**
 * Get minimum slot start time for today (current time + 30 minutes, rounded to next 30-minute interval)
 * This matches the backend logic
 * @returns {number} Minimum slot start time in minutes since midnight
 */
function getMinSlotStartMinutes() {
  const now = new Date();
  const currentMinutesOfDay = now.getHours() * 60 + now.getMinutes();
  // Minimum slot start time: current time + 30 minutes buffer, rounded up to next 30-minute interval
  const minSlotStartMinutes = Math.ceil((currentMinutesOfDay + 30) / 30) * 30;
  return minSlotStartMinutes;
}

/**
 * Check if a date is today
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean}
 */
function isToday(dateStr) {
  const today = new Date().toISOString().split('T')[0];
  return dateStr === today;
}

/**
 * Calculate available time slots from availability data
 * @param {Object} availability - Availability configuration
 * @param {string[]} availability.dates - Array of date strings (YYYY-MM-DD)
 * @param {string} availability.startTime - Start time (HH:MM)
 * @param {string} availability.endTime - End time (HH:MM)
 * @param {number} availability.interviewDuration - Duration in minutes
 * @param {number} availability.bufferTime - Buffer time in minutes between slots (default: 0)
 * @returns {Array<{date: string, startTime: string, endTime: string}>}
 */
export function calculateTimeSlots(availability) {
  const slots = [];
  // Ensure bufferTime is always a number (handle empty string, null, undefined)
  const bufferTime = typeof availability.bufferTime === 'number' ? availability.bufferTime : 
                     (availability.bufferTime === '' || availability.bufferTime === null || availability.bufferTime === undefined) ? 0 : 
                     parseInt(availability.bufferTime, 10) || 0;
  
  const { dates, startTime, endTime, interviewDuration } = availability;
  
  const [startHour, startMinute] = startTime.split(":").map(Number);
  const [endHour, endMinute] = endTime.split(":").map(Number);
  
  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;
  
  // Get minimum slot start time for today (only used if date is today)
  const minSlotStartMinutes = getMinSlotStartMinutes();
  
  for (const date of dates) {
    let currentMinutes = startMinutes;
    const isTodayDate = isToday(date);
    
    while (currentMinutes + interviewDuration <= endMinutes) {
      // For today's date, skip slots that are in the past (less than 30 minutes from now)
      if (isTodayDate && currentMinutes < minSlotStartMinutes) {
        // Skip this slot, move to next
        currentMinutes += interviewDuration + bufferTime;
        continue;
      }
      
      const slotStartHour = Math.floor(currentMinutes / 60);
      const slotStartMinute = currentMinutes % 60;
      const slotEndMinutes = currentMinutes + interviewDuration;
      const slotEndHour = Math.floor(slotEndMinutes / 60);
      const slotEndMinute = slotEndMinutes % 60;
      
      const slotStartTime = `${slotStartHour.toString().padStart(2, "0")}:${slotStartMinute.toString().padStart(2, "0")}`;
      
      slots.push({
        date,
        startTime: slotStartTime,
        endTime: `${slotEndHour.toString().padStart(2, "0")}:${slotEndMinute.toString().padStart(2, "0")}`,
      });
      
      // Move to next slot: add duration + buffer_time
      currentMinutes += interviewDuration + bufferTime;
    }
  }
  
  return slots;
}

/**
 * Convert time string to minutes
 * @param {string} time - Time in HH:MM format
 * @returns {number} Minutes since midnight
 */
function timeToMinutes(time) {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

/**
 * Check if a time string is valid and non-empty
 * @param {string|undefined} time - Time string
 * @returns {boolean}
 */
function isValidTime(time) {
  return !!(time && time.trim() && time.includes(":"));
}

/**
 * Check if a slot matches a candidate's preferences
 * @param {Object} slot - Time slot
 * @param {Object} candidate - Candidate data
 * @returns {boolean}
 */
function slotMatchesPreferences(slot, candidate) {
  const hasDatePrefs = candidate.preferredDates && candidate.preferredDates.length > 0;
  const hasStartPref = isValidTime(candidate.preferredStartTime);
  const hasEndPref = isValidTime(candidate.preferredEndTime);
  
  // No preferences means any slot is acceptable
  if (!hasDatePrefs && !hasStartPref && !hasEndPref) {
    return true;
  }
  
  // Check date preference
  if (hasDatePrefs && !candidate.preferredDates.includes(slot.date)) {
    return false;
  }
  
  // Check time preference
  const slotStart = timeToMinutes(slot.startTime);
  const slotEnd = timeToMinutes(slot.endTime);
  
  if (hasStartPref) {
    const prefStart = timeToMinutes(candidate.preferredStartTime);
    if (!isNaN(prefStart) && slotStart < prefStart) return false;
  }
  
  if (hasEndPref) {
    const prefEnd = timeToMinutes(candidate.preferredEndTime);
    if (!isNaN(prefEnd) && slotEnd > prefEnd) return false;
  }
  
  return true;
}

/**
 * Score a slot for a candidate (higher = better match)
 * @param {Object} slot - Time slot
 * @param {Object} candidate - Candidate data
 * @returns {number} Score
 */
function scoreSlotForCandidate(slot, candidate) {
  let score = 0;
  
  const hasDatePrefs = candidate.preferredDates && candidate.preferredDates.length > 0;
  const hasStartPref = isValidTime(candidate.preferredStartTime);
  const hasEndPref = isValidTime(candidate.preferredEndTime);
  
  // Bonus for preferred date match
  if (hasDatePrefs && candidate.preferredDates.includes(slot.date)) {
    score += 10;
  }
  
  // Bonus for time within preferred window
  if (hasStartPref || hasEndPref) {
    const slotStart = timeToMinutes(slot.startTime);
    const slotEnd = timeToMinutes(slot.endTime);
    
    const prefStart = hasStartPref 
      ? timeToMinutes(candidate.preferredStartTime) 
      : 0;
    const prefEnd = hasEndPref 
      ? timeToMinutes(candidate.preferredEndTime) 
      : 24 * 60;
    
    if (!isNaN(prefStart) && !isNaN(prefEnd) && slotStart >= prefStart && slotEnd <= prefEnd) {
      score += 5;
    }
  }
  
  return score;
}

/**
 * Assign candidates to time slots
 * @param {Array<Object>} candidates - Array of candidate data
 * @param {Array<Object>} slots - Array of time slots
 * @returns {Array<{candidate: Object, slot: Object}>}
 */
export function assignCandidatesToSlots(candidates, slots) {
  const interviews = [];
  const usedSlotIndices = new Set();
  
  // Sort candidates by those with preferences first (they're harder to place)
  const sortedCandidates = [...candidates].sort((a, b) => {
    const aHasPrefs = (a.preferredDates?.length || 0) + (isValidTime(a.preferredStartTime) ? 1 : 0) + (isValidTime(a.preferredEndTime) ? 1 : 0);
    const bHasPrefs = (b.preferredDates?.length || 0) + (isValidTime(b.preferredStartTime) ? 1 : 0) + (isValidTime(b.preferredEndTime) ? 1 : 0);
    return bHasPrefs - aHasPrefs; // More preferences first
  });
  
  for (const candidate of sortedCandidates) {
    let bestSlotIndex = -1;
    let bestScore = -1;
    
    // Find the best available slot for this candidate
    for (let i = 0; i < slots.length; i++) {
      if (usedSlotIndices.has(i)) continue;
      
      const slot = slots[i];
      
      // First priority: exact match with preferences
      if (slotMatchesPreferences(slot, candidate)) {
        const score = scoreSlotForCandidate(slot, candidate);
        if (score > bestScore) {
          bestScore = score;
          bestSlotIndex = i;
        }
      }
    }
    
    // If no preferred slot found, just use the first available
    if (bestSlotIndex === -1) {
      for (let i = 0; i < slots.length; i++) {
        if (!usedSlotIndices.has(i)) {
          bestSlotIndex = i;
          break;
        }
      }
    }
    
    // Assign the slot if found
    if (bestSlotIndex !== -1) {
      usedSlotIndices.add(bestSlotIndex);
      interviews.push({
        candidate,
        slot: slots[bestSlotIndex],
      });
    }
  }
  
  return interviews;
}

/**
 * Calculate maximum number of candidates that can be scheduled
 * @param {Object} availability - Availability configuration
 * @returns {number} Maximum number of candidates
 */
export function calculateMaxCandidates(availability) {
  const slots = calculateTimeSlots(availability);
  return slots.length;
}

/**
 * Suggest an optimal end time based on the number of candidates, duration, and buffer.
 * This auto-adjusts the time window so that the end time accommodates exactly
 * the required number of candidates (with a small padding of 1 extra slot).
 * The suggested end time won't go beyond the maximum end of day (18:00).
 *
 * @param {string} startTime - Start time in HH:MM format
 * @param {number} candidateCount - Number of candidates to schedule
 * @param {number} duration - Interview duration in minutes
 * @param {number} bufferTime - Buffer time between interviews in minutes (default: 0)
 * @param {number} dateCount - Number of selected dates (default: 1)
 * @returns {string} Suggested end time in HH:MM format
 */
export function suggestEndTime(startTime, candidateCount, duration, bufferTime = 0, dateCount = 1) {
  if (!startTime || !candidateCount || candidateCount <= 0 || !duration || duration <= 0) {
    return '17:00'; // default fallback
  }

  const [startHour, startMinute] = startTime.split(':').map(Number);
  const startMinutes = startHour * 60 + startMinute;
  const maxEndMinutes = 18 * 60; // 18:00 hard cap

  // Calculate candidates per day (distribute across dates, rounded up)
  const candidatesPerDay = Math.ceil(candidateCount / Math.max(dateCount, 1));

  // Calculate exact slots needed for the candidate count (no extra padding)
  const slotsNeeded = candidatesPerDay;

  // Total minutes needed: (slotsNeeded * duration) + ((slotsNeeded - 1) * bufferTime)
  const totalMinutesNeeded = (slotsNeeded * duration) + ((slotsNeeded - 1) * Math.max(bufferTime, 0));

  let suggestedEndMinutes = startMinutes + totalMinutesNeeded;

  // Cap at max end of day
  if (suggestedEndMinutes > maxEndMinutes) {
    suggestedEndMinutes = maxEndMinutes;
  }

  // Ensure at least 1 slot is possible
  if (suggestedEndMinutes < startMinutes + duration) {
    suggestedEndMinutes = Math.min(startMinutes + duration, maxEndMinutes);
  }

  const endHour = Math.floor(suggestedEndMinutes / 60);
  const endMinute = suggestedEndMinutes % 60;
  return `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
}
